import type { CSSProperties, ReactElement } from 'react'

export type SentryFallbackProps = {
  error: unknown
  componentStack: string
  eventId: string
  resetError: () => void
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  minHeight: '60vh',
  padding: 24,
  textAlign: 'center',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  color: '#1a1a1a',
}

const buttonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  color: '#fff',
  background: '#e8590c',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
}

const detailsStyle: CSSProperties = {
  maxWidth: 720,
  width: '100%',
  textAlign: 'left',
  background: '#f1f3f5',
  borderRadius: 8,
  padding: 16,
  fontSize: 12,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

export function SentryErrorFallback({
  error,
  componentStack,
  eventId,
  resetError,
}: SentryFallbackProps): ReactElement {
  const isDev = import.meta.env.DEV

  return (
    <div role="alert" style={containerStyle}>
      <h2 style={{ margin: 0, fontSize: 22 }}>Something went wrong.</h2>
      <p style={{ margin: 0, color: '#495057' }}>
        We received the error report. Please try again.
      </p>

      {eventId ? (
        <p style={{ margin: 0, fontSize: 12, color: '#868e96' }}>
          Error ID: <code>{eventId}</code>
        </p>
      ) : null}

      <button type="button" style={buttonStyle} onClick={resetError}>
        Try again
      </button>

      {isDev ? (
        <pre style={detailsStyle}>
          {getErrorMessage(error)}
          {componentStack ? `\n\n${componentStack}` : ''}
        </pre>
      ) : null}
    </div>
  )
}
